/**
 * Variant Selector Enhancement
 * Handles SELECT/SELECTED button text updates for variant picker buttons with images
 */

class VariantSelectorEnhancement {
  constructor() {
    this.initialized = false;
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // Find all variant selects containers
    this.initializeVariantSelectors();
    
    // Listen for Shopify's variant change events
    this.setupShopifyEventListeners();
    
    // Set up mutation observer to handle DOM updates
    this.setupMutationObserver();
  }

  initializeVariantSelectors() {
    const variantSelects = document.querySelectorAll('variant-selects');
    
    variantSelects.forEach(container => {
      // Remove existing listeners to avoid duplicates
      const existingHandler = container._variantChangeHandler;
      if (existingHandler) {
        container.removeEventListener('change', existingHandler);
      }
      
      // Create new handler and store reference
      const handler = (event) => this.handleVariantChange(event);
      container._variantChangeHandler = handler;
      container.addEventListener('change', handler);
      
      // Initialize button states
      this.initializeButtonStates(container);

      // Re-init mat tabs after variant selector refresh
      if (typeof initMatTabs === 'function') initMatTabs();
      if (typeof syncMatSelections === 'function') syncMatSelections();
      if (typeof activateTabForCheckedMat === 'function') activateTabForCheckedMat();

    });
    
    this.initialized = true;
  }

  setupShopifyEventListeners() {
    // Listen for Shopify's pubsub events if available
    if (typeof subscribe === 'function') {
      subscribe('variant-change', () => {
        // Re-initialize after variant change
        setTimeout(() => this.initializeVariantSelectors(), 100);
      });
      
      subscribe('product-update', () => {
        // Re-initialize after product update
        setTimeout(() => this.initializeVariantSelectors(), 100);
      });
    }
    
    // Also listen for custom events that might be fired
    document.addEventListener('variant:change', () => {
      setTimeout(() => this.initializeVariantSelectors(), 100);
    });
    
    document.addEventListener('product:loaded', () => {
      setTimeout(() => this.initializeVariantSelectors(), 100);
    });
  }

  setupMutationObserver() {
    // Watch for changes to the product info container
    const productContainers = document.querySelectorAll('[id*="ProductInfo"], .product__info-container');
    
    productContainers.forEach(container => {
      const observer = new MutationObserver((mutations) => {
        let shouldReinit = false;
        
        mutations.forEach(mutation => {
          // Check if variant selector elements were added/modified
          if (mutation.type === 'childList') {
            const hasVariantSelectors = Array.from(mutation.addedNodes).some(node => 
              node.nodeType === 1 && (
                node.querySelector && node.querySelector('variant-selects') ||
                node.matches && node.matches('variant-selects')
              )
            );
            if (hasVariantSelectors) {
              shouldReinit = true;
            }
          }
        });
        
        if (shouldReinit) {
          setTimeout(() => this.initializeVariantSelectors(), 50);
        }
      });
      
      observer.observe(container, {
        childList: true,
        subtree: true
      });
    });
  }

  handleVariantChange(event) {
    const target = event.target;
    
    // Only handle radio button changes for pill-style variant selectors
    if (target.tagName === 'INPUT' && target.type === 'radio') {
      this.updateVariantSelectButtons(target);
    }
  }

  updateVariantSelectButtons(selectedInput) {
    const fieldset = selectedInput.closest('.product-form__input--pill');
    if (!fieldset) return;
    
    // Use a more robust approach to update button states
    this.updateButtonStatesInFieldset(fieldset);
  }

  updateButtonStatesInFieldset(fieldset) {
    // Reset all buttons in this fieldset to "SELECT"
    const allButtons = fieldset.querySelectorAll('.variant-select-btn');
    allButtons.forEach(btn => {
      btn.textContent = 'SELECT';
    });
    
    // Find the checked input and set its button to "SELECTED"
    const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
    if (checkedInput) {
      const selectedLabel = checkedInput.nextElementSibling;
      const selectedBtn = selectedLabel?.querySelector('.variant-select-btn');
      if (selectedBtn) {
        selectedBtn.textContent = 'SELECTED';
      }
    }
  }

  initializeButtonStates(container) {
    // Set initial states for all variant selector buttons
    const fieldsets = container.querySelectorAll('.product-form__input--pill');
    
    fieldsets.forEach(fieldset => {
      this.updateButtonStatesInFieldset(fieldset);
    });
    
    // Also handle any individual pill inputs not in fieldsets
    const pillInputs = container.querySelectorAll('.product-form__input--pill input[type="radio"]');
    
    pillInputs.forEach(input => {
      const label = input.nextElementSibling;
      const btn = label?.querySelector('.variant-select-btn');
      
      if (btn) {
        if (input.checked) {
          btn.textContent = 'SELECTED';
        } else {
          btn.textContent = 'SELECT';
        }
      }
    });
  }
}

// Initialize when the script loads
new VariantSelectorEnhancement();
